import { createOptimizedPicture } from '../../../../scripts/aem.js';
import { button, div, p, span } from '../../../../scripts/dom-builder.js';
import { formatPhraseWithParams } from '../../../../scripts/utils/text-utils.js';
import {
  IMAGE_DIMENSIONS,
  SELECTORS,
  VIEW_TYPES,
} from '../product-tour.constants.js';
import {
  AA_CONSTANTS,
  appendLoaderToContainer,
  decorateNextSVG,
  decoratePreviousSVG,
  UNTITLED_SECTION_PLACEHOLDER_KEY,
} from './common.js';
import updateImagePath from './update-image-path.js';

const TIMEOUTS = {
  DOM_READY: 0, // setTimeout with 0 for next tick
};

const ERROR_MESSAGES = {
  GET_VIEW_TYPE: 'getViewType must be implemented by subclass',
  GET_SELECTORS: 'getSelectors must be implemented by subclass',
  IS_VALID_SECTION: 'isValidSection must be implemented by subclass',
  CLOSE_SECTION: 'closeSection must be implemented by subclass',
  OPEN_SECTION: 'openSection must be implemented by subclass',
  UPDATE_STEP: 'updateStep must be implemented by subclass',
};

const COUNTER_LABEL_PLACEHOLDER_KEY =
  'MXP.Navigation.Accessibility.Step.Counter.Status.Template';
const STEP_TITLE_PLACEHOLDER_KEY = 'MXP.Navigation.Accessibility.Step.Title';

/**
 * Enhanced base class for Product Tour implementations
 * Contains shared functionality and common patterns
 */
/* eslint-disable no-unused-vars, class-methods-use-this */
class ProductTourBaseEnhanced {
  constructor(i18next) {
    this.i18next = i18next;
    this.AA_CONSTANTS = AA_CONSTANTS;
    this.viewType = this.getViewType();

    // Cache frequently accessed selectors
    this.selectors = this.getSelectors();

    this.productTourCleanup = [];

    // Bind methods to prevent context loss
    this.updateStep = this.updateStep.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
  }

  /**
   * Abstract method to determine view type
   * @returns {string} VIEW_TYPES.DESKTOP or VIEW_TYPES.MOBILE
   */
  getViewType() {
    throw new Error(ERROR_MESSAGES.GET_VIEW_TYPE);
  }

  /**
   * Get selectors specific to the view type
   * @returns {Object} Object containing view-specific selectors
   */
  getSelectors() {
    throw new Error(ERROR_MESSAGES.GET_SELECTORS);
  }

  /**
   * Get standardized Adobe Analytics attributes
   * @param {string} linkName - The name of the link for analytics
   * @param {string} linkTitle - The title of the link for analytics
   * @returns {Object} Object containing AA attributes
   */
  getAAAttributes(linkName, linkTitle) {
    return {
      'data-analytics-aa-tracking': 'true',
      'data-analytics-aa-region': this.AA_CONSTANTS.REGION,
      'data-analytics-aa-mediatitle':
        this.viewType === VIEW_TYPES.DESKTOP
          ? this.AA_CONSTANTS.MEDIA_TITLE_DESKTOP
          : this.AA_CONSTANTS.MEDIA_TITLE_MOBILE,
      'data-analytics-aa-mediatype': this.AA_CONSTANTS.MEDIA_TYPE,
      'data-analytics-aa-linkname': linkName,
      'data-analytics-aa-linktitle': linkTitle || linkName,
    };
  }

  /**
   * Shared step content extraction logic
   * @param {Object} data - Section data
   * @param {number} stepNumber - Step number
   * @returns {Object} Extracted step content
   */
  extractStepContent(data, stepNumber) {
    if (!this.validateStepData(data, stepNumber)) {
      return null;
    }

    const stepItem = data.step[stepNumber - 1];
    return {
      stepItem,
      sectionTitle:
        data.section_title ||
        stepItem.title ||
        this.i18next.t(UNTITLED_SECTION_PLACEHOLDER_KEY, 'Untitled'),
      stepDescription:
        stepItem.description ||
        this.i18next.t('MXP.Section.Step.Description.Text', ''),
    };
  }

  /**
   * Validate step data
   * @param {Object} data - Section data
   * @param {number} stepNumber - Step number
   * @returns {boolean} Whether data is valid
   */
  validateStepData(data, stepNumber) {
    if (!data?.step || !Array.isArray(data.step)) {
      return false;
    }

    return stepNumber >= 1 && stepNumber <= data.step.length;
  }

  /**
   * Create optimized picture element with consistent settings
   * @param {string} imagePath - Image path
   * @param {string} altText - Alt text
   * @param {boolean} isPlaceholder - Whether this is a placeholder image
   * @returns {Element} Picture element
   */
  createOptimizedImage(imagePath, altText, isPlaceholder = false) {
    const picture = createOptimizedPicture(imagePath, altText, false, [
      {
        media: '(min-width: 640px)',
        width: IMAGE_DIMENSIONS.DESKTOP_WIDTH.toString(),
      },
      { width: IMAGE_DIMENSIONS.MOBILE_WIDTH.toString() },
    ]);

    updateImagePath(picture);

    const img = picture.querySelector('img');
    if (isPlaceholder) {
      img.classList.add('gt_placeholder');
    }
    img.classList.add('gt_image');

    return picture;
  }

  /**
   * Shared navigation logic - handles step transitions
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   * @param {string} direction - 'next' or 'prev'
   */
  handleNavigation(section, data, direction) {
    if (!section?.dataset) {
      return;
    }

    const currentStep = Number.parseInt(section.dataset.step, 10) || 1;
    const sectionIndex = Number.parseInt(section.dataset.section, 10);

    if (Number.isNaN(sectionIndex) || !data?.section?.[sectionIndex]) {
      return;
    }

    if (direction === 'next') {
      this.handleNextNavigation(section, data, currentStep, sectionIndex);
    } else if (direction === 'prev') {
      this.handlePrevNavigation(section, data, currentStep, sectionIndex);
    }
  }

  /**
   * Handle progression to next step within current section
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   * @param {number} currentStep - Current step number
   * @param {number} sectionIndex - Current section index
   */
  handleNormalStepProgression(section, data, currentStep, sectionIndex) {
    this.updateStep(section, data.section[sectionIndex], currentStep + 1, data);
  }

  /**
   * Handle final step modal display
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   * @param {number} sectionIndex - Current section index
   * @param {number} actualStepsInSection - Number of actual steps in section
   */
  handleFinalStepInLastSection(
    section,
    data,
    sectionIndex,
    actualStepsInSection,
  ) {
    const extraStepNumber = actualStepsInSection + 1;
    const effectiveTotalSteps = actualStepsInSection + 1;

    section.dataset.step = extraStepNumber;

    // Update text content for the extra step (keep current content but update step indicator)
    const stepContent = this.extractStepContent(
      data.section[sectionIndex],
      actualStepsInSection,
    );
    if (stepContent) {
      const { stepItem, sectionTitle, stepDescription } = stepContent;
      this.updateTextContent(
        section,
        sectionTitle,
        stepDescription,
        extraStepNumber,
        effectiveTotalSteps,
      );
      // Update image to show the last step's image
      this.updateStepImage(section, stepItem);
    }

    this.updateNavButtonsAriaLabels(section, data.section[sectionIndex], data);
    this.showFinalStepModal(section, data);
  }

  /**
   * Handle next navigation logic
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   * @param {number} currentStep - Current step number
   * @param {number} sectionIndex - Current section index
   */
  handleNextNavigation(section, data, currentStep, sectionIndex) {
    const isLastSection = sectionIndex === data.section.length - 1;
    const actualStepsInSection = data.section[sectionIndex].step.length;

    if (currentStep < actualStepsInSection) {
      this.handleNormalStepProgression(
        section,
        data,
        currentStep,
        sectionIndex,
      );
    } else if (isLastSection && currentStep === actualStepsInSection) {
      this.handleFinalStepInLastSection(
        section,
        data,
        sectionIndex,
        actualStepsInSection,
      );
    } else {
      this.moveToNextSection(section, data, sectionIndex);
    }
  }

  /**
   * Handle previous navigation logic
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   * @param {number} currentStep - Current step number
   * @param {number} sectionIndex - Current section index
   */
  handlePrevNavigation(section, data, currentStep, sectionIndex) {
    // Hide modal if it's open (especially when moving from extra step)
    import('./common.js')
      .then(({ closeModalIfOpen }) => {
        closeModalIfOpen(this.viewType);
      })
      .catch(() => {
        // Continue without modal close if import fails
      });

    if (currentStep > 1) {
      this.updateStep(
        section,
        data.section[sectionIndex],
        currentStep - 1,
        data,
      );
    } else {
      this.moveToPrevSection(section, data, sectionIndex);
    }
  }

  /**
   * Show final step modal for last section
   * @param {Element} section - Current section
   * @param {Object} fragmentData - Fragment data containing call_to_action
   */
  showFinalStepModal(section, fragmentData) {
    // Import and show modal
    import('./common.js')
      .then(({ openModalWithActions }) => {
        openModalWithActions(
          fragmentData,
          this.viewType === VIEW_TYPES.DESKTOP,
        );
      })
      .catch(() => {
        // Fallback: just disable next button if modal fails to load
        const { nextButton } = this.getNavButtons(section);
        if (nextButton) {
          this.setDisabledState(nextButton, true);
        }
      });
  }

  /**
   * Move to next section (shared logic)
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   */
  moveToNextSection(section, data) {
    const nextSection = section.nextElementSibling;
    if (nextSection && this.isValidSection(nextSection)) {
      // Hide modal if it's open
      import('./common.js')
        .then(({ closeModalIfOpen }) => {
          closeModalIfOpen(this.viewType);
        })
        .catch(() => {
          // Continue without modal close if import fails
        });

      this.closeSection(section);
      this.openSection(nextSection);

      const nextSectionIndex = Number.parseInt(nextSection.dataset.section, 10);
      this.updateStep(nextSection, data.section[nextSectionIndex], 1, data);
      this.trackSectionTransition(data.section[nextSectionIndex]);
    }
  }

  /**
   * Move to previous section (shared logic)
   * @param {Element} section - Current section
   * @param {Object} data - Fragment data
   */
  moveToPrevSection(section, data) {
    const prevSection = section.previousElementSibling;
    if (prevSection && this.isValidSection(prevSection)) {
      // Hide modal if it's open
      import('./common.js')
        .then(({ closeModalIfOpen }) => {
          closeModalIfOpen(this.viewType);
        })
        .catch(() => {
          // Continue without modal close if import fails
        });

      this.closeSection(section);
      this.openSection(prevSection);

      const prevSectionIndex = Number.parseInt(prevSection.dataset.section, 10);
      if (Number.isNaN(prevSectionIndex)) {
        // prevSection is the overview element which has no data-section attribute;
        // its content was rendered at decoration time and needs no step update
        return;
      }
      const prevSectionSteps = data.section[prevSectionIndex].step.length;
      const isLastSection = prevSectionIndex === data.section.length - 1;
      const stepToShow = isLastSection
        ? prevSectionSteps + 1
        : prevSectionSteps;
      this.updateStep(
        prevSection,
        data.section[prevSectionIndex],
        stepToShow,
        data,
      );
      this.trackSectionTransition(data.section[prevSectionIndex]);
    }
  }

  /**
   * Track section transition for analytics
   * @param {Object} sectionData - Section data
   */
  trackSectionTransition(sectionData) {
    const sectionTitle =
      sectionData?.section_title ||
      sectionData?.step?.[0]?.title ||
      this.i18next.t(UNTITLED_SECTION_PLACEHOLDER_KEY, 'Untitled');

    // eslint-disable-next-line no-undef -- SonarQube prefers globalThis
    // but ESLint config doesn't support it
    if (window.adobeDataLayer) {
      const activityMapRegion = [
        this.AA_CONSTANTS.REGION,
        this.AA_CONSTANTS.MEDIA_TYPE,
        this.viewType === VIEW_TYPES.DESKTOP
          ? this.AA_CONSTANTS.MEDIA_TITLE_DESKTOP
          : this.AA_CONSTANTS.MEDIA_TITLE_MOBILE,
      ]
        .join('|')
        .toLowerCase();
      const activityMapLink = [sectionTitle, sectionTitle]
        .join('|')
        .toLowerCase();

      // eslint-disable-next-line no-undef -- SonarQube prefers globalThis
      // but ESLint config doesn't support it
      window.adobeDataLayer.push(
        {
          event: 'activityMap',
          activityMap: {
            region: activityMapRegion,
            link: activityMapLink,
          },
        },
        {
          event: 'stlBeaconReady',
        },
      );
    }
  }

  /**
   * Create step indicator text with proper formatting
   * @param {number} stepNum - Current step number
   * @param {number} totalSteps - Total steps
   * @returns {Array} Array of elements for step indicator
   */
  createStepIndicator(stepNum, totalSteps) {
    const counterLabel = this.i18next.t(COUNTER_LABEL_PLACEHOLDER_KEY, '');
    return [
      this.i18next.t(STEP_TITLE_PLACEHOLDER_KEY, 'Step'),
      ' ',
      span(stepNum),
      ' ',
      counterLabel
        ? formatPhraseWithParams(counterLabel, { steps: totalSteps })
        : '',
    ];
  }

  /**
   * Attach event listeners with proper cleanup
   * @param {Element} block - Block element
   * @param {Function} attachFunction - Function to attach listeners,
   * should return cleanup functions array
   */
  attachListenersWithCleanup(block, attachFunction) {
    setTimeout(() => {
      const cleanupFunctions = attachFunction();

      // Store cleanup functions for later use
      if (!this.productTourCleanup) {
        this.productTourCleanup = [];
      }

      // Add cleanup functions to the array
      if (Array.isArray(cleanupFunctions)) {
        this.productTourCleanup.push(...cleanupFunctions);
      } else if (typeof cleanupFunctions === 'function') {
        this.productTourCleanup.push(cleanupFunctions);
      }
    }, TIMEOUTS.DOM_READY);
  }

  /**
   * Clean up all event listeners for a block
   */
  cleanupEventListeners() {
    if (this.productTourCleanup && Array.isArray(this.productTourCleanup)) {
      // eslint-disable-next-line no-restricted-syntax -- SonarQube prefers for...of
      // but ESLint config doesn't allow it
      this.productTourCleanup.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          try {
            cleanup();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Cleanup function failed: ${error.message}`);
          }
        }
      });
      this.productTourCleanup = [];
    }
  }

  // Abstract methods that must be implemented by subclasses

  /**
   * Check if element is a valid section for this view type
   * @abstract
   * @param {Element} _element - Element to check
   * @returns {boolean} Whether element is valid section
   */
  isValidSection(_element) {
    throw new Error(ERROR_MESSAGES.IS_VALID_SECTION);
  }

  /**
   * Close a section
   * @abstract
   * @param {Element} _section - Section to close (unused in base)
   */
  closeSection(_section) {
    throw new Error(ERROR_MESSAGES.CLOSE_SECTION);
  }

  /**
   * Open a section
   * @abstract
   * @param {Element} _section - Section to open (unused in base)
   */
  openSection(_section) {
    throw new Error(ERROR_MESSAGES.OPEN_SECTION);
  }

  /**
   * Calculate section information for step update
   * @param {Element} section - Section element
   * @param {Object} fragmentData - Fragment data
   * @param {number} stepNumber - Step number
   * @returns {Object} Section information
   */
  calculateSectionInfo(section, fragmentData, stepNumber) {
    const sectionIndex = Number.parseInt(section.dataset.section, 10);
    const isLastSection = sectionIndex === fragmentData.section.length - 1;
    const actualStepsInSection =
      fragmentData.section[sectionIndex]?.step?.length || 0;

    const effectiveStepNumber =
      isLastSection && stepNumber > actualStepsInSection
        ? actualStepsInSection
        : stepNumber;

    return {
      sectionIndex,
      isLastSection,
      actualStepsInSection,
      effectiveStepNumber,
    };
  }

  /**
   * Handle overview content update
   * @param {Element} section - Section element
   * @param {Object} data - Section data
   * @returns {boolean} Whether overview was handled
   */
  handleOverviewContent(section, data) {
    const overviewData = data?.overview_screenshot;
    const sectionIndexAttr = section?.dataset?.section;

    if (
      overviewData &&
      (sectionIndexAttr === null || sectionIndexAttr === undefined)
    ) {
      const overviewStepItem = { screen: data.overview_screenshot.screen };
      const overviewTitle = this.i18next.t(
        'MXP.Overview.Section.Title.Text',
        'Overview',
      );
      const overviewDesc =
        data.overview_text ||
        this.i18next.t('MXP.Section.Step.Description.Text', '');

      this.updateTextContent(section, overviewTitle, overviewDesc, 1, 1);
      this.updateStepImage(section, overviewStepItem);
      return true;
    }

    return false;
  }

  /**
   * Handle final step modal display
   * @param {Element} section - Section element
   * @param {Object} data - Section data
   * @param {Object} fragmentData - Fragment data
   * @param {number} stepNumber - Step number
   */
  handleFinalStepModal(section, data, fragmentData, stepNumber) {
    const currentSectionIndex = Number.parseInt(section.dataset.section, 10);
    const isCurrentSectionLast =
      currentSectionIndex === fragmentData.section.length - 1;
    const actualStepsCount = data.step.length;

    if (isCurrentSectionLast && stepNumber > actualStepsCount) {
      const lastStepContent = this.extractStepContent(data, actualStepsCount);
      if (lastStepContent) {
        const { stepItem: lastStepItem } = lastStepContent;
        this.updateStepImage(section, lastStepItem);
      }
      this.showFinalStepModal(section, fragmentData);
    }
  }

  /**
   * Update step content and image - now implemented in base class
   * @param {Element} section - Section element
   * @param {Object} data - Section data
   * @param {number} stepNumber - Step number
   * @param {Object} fragmentData - Fragment data
   */
  updateStep(section, data, stepNumber, fragmentData) {
    const sectionInfo = this.calculateSectionInfo(
      section,
      fragmentData,
      stepNumber,
    );
    const { isLastSection, actualStepsInSection, effectiveStepNumber } =
      sectionInfo;

    const stepContent = this.extractStepContent(data, effectiveStepNumber);

    if (!stepContent) {
      if (this.handleOverviewContent(section, data)) {
        return;
      }
      return;
    }

    const { stepItem, sectionTitle, stepDescription } = stepContent;
    const effectiveTotalSteps = isLastSection
      ? actualStepsInSection + 1
      : actualStepsInSection;

    // Update content and state
    this.updateTextContent(
      section,
      sectionTitle,
      stepDescription,
      stepNumber,
      effectiveTotalSteps,
    );
    this.updateStepImage(section, stepItem);
    section.dataset.step = stepNumber;

    // Update navigation and handle final step
    this.updateNavButtonsAriaLabels(section, data, fragmentData);
    this.handleFinalStepModal(section, data, fragmentData, stepNumber);
  }

  /**
   * Attach listener to a single button
   * @param {Element} buttonElement - Button element
   * @param {Element} section - Section element
   * @param {Object} data - Data object
   * @param {string} direction - Direction ('prev' or 'next')
   * @returns {Function} Cleanup function
   */
  attachSingleButtonListener(buttonElement, section, data, direction) {
    const handler = () => {
      this.handleNavigation(section, data, direction);
    };

    buttonElement.addEventListener('click', handler);
    return () => {
      buttonElement.removeEventListener('click', handler);
    };
  }

  /**
   * Attach listeners to navigation buttons in a section
   * @param {Element} section - Section element
   * @param {Object} data - Data object
   * @returns {Array} Array of cleanup functions
   */
  attachSectionListeners(section, data) {
    const cleanupFunctions = [];
    const prevButton = section.querySelector('.tour-prev');
    const nextButton = section.querySelector('.tour-next');

    if (prevButton) {
      cleanupFunctions.push(
        this.attachSingleButtonListener(prevButton, section, data, 'prev'),
      );
    }

    if (nextButton) {
      cleanupFunctions.push(
        this.attachSingleButtonListener(nextButton, section, data, 'next'),
      );
    }

    return cleanupFunctions;
  }

  /**
   * Attaches navigation listeners for both desktop and mobile views
   * @param {Element} block - The block element
   * @param {Object} data - The data object
   */
  attachNavigationListeners(block, data) {
    this.attachListenersWithCleanup(block, () => {
      const sections = block.querySelectorAll(this.selectors.SECTION_SELECTOR);
      if (!sections.length) {
        return [];
      }

      const cleanupFunctions = [];

      // eslint-disable-next-line no-restricted-syntax -- SonarQube prefers for...of
      // but ESLint config doesn't allow it
      sections.forEach((section) => {
        const sectionCleanup = this.attachSectionListeners(section, data);
        cleanupFunctions.push(...sectionCleanup);
      });

      return cleanupFunctions;
    });
  }

  /**
   * Helper to set aria-disabled and disabled properties safely on an element
   * @param {Element} el - Element (button) to update
   * @param {boolean} disabled - true to disable, false to enable
   */
  setDisabledState(el, disabled) {
    if (!el || typeof el.setAttribute !== 'function') {
      return;
    }
    el.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    el.disabled = !!disabled;
  }

  /**
   * Return list of candidate focusable elements in document (visible + tabbable)
   * @returns {HTMLElement[]}
   */
  getFocusableCandidates() {
    const selector = [
      'a[href]',
      'area[href]',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    const productTourBlock = document.querySelector(SELECTORS.PRODUCT_TOUR);
    if (!productTourBlock) {
      return [];
    }
    return Array.from(productTourBlock.querySelectorAll(selector)).filter(
      (el) => {
        // eslint-disable-next-line no-undef -- SonarQube prefers globalThis
        // but ESLint config doesn't support it
        const style = window.getComputedStyle(el);
        const visible =
          style.visibility !== 'hidden' && style.display !== 'none';
        const hasSize =
          el.offsetWidth > 0 ||
          el.offsetHeight > 0 ||
          el.getClientRects().length > 0;
        return visible && hasSize;
      },
    );
  }

  /**
   * Move focus relative to a given element.
   * @param {HTMLElement} el - reference element
   * @param {'next'|'prev'} direction - desired direction
   * @return {HTMLElement|null} - the newly focused element or null
   */
  moveFocus(el, direction = 'next') {
    if (!el) {
      return null;
    }
    const dir = direction === 'prev' ? 'prev' : 'next';

    const list = this.getFocusableCandidates();
    const idx = list.indexOf(el);
    if (idx < 0) {
      return null;
    }
    const moveIndex = dir === 'next' ? idx + 1 : idx - 1;
    if (moveIndex >= 0 && moveIndex < list.length) {
      try {
        list[moveIndex].focus();
        return list[moveIndex];
      } catch (focusError) {
        // Focus operation failed - this can happen with hidden/disabled elements
        // Continue gracefully without logging to avoid console spam
        // eslint-disable-next-line no-console
        console.debug('Focus operation failed:', focusError.message);
        return null;
      }
    }
    return null;
  }

  /**
   * Update text content in section (view-specific implementation)
   * @abstract
   * @param {Element} _section - Section element (unused in base)
   * @param {string} _sectionTitle - Section title (unused in base)
   * @param {string} _stepDescription - Step description (unused in base)
   * @param {number} _stepNumber - Step number (unused in base)
   * @param {number} _totalSteps - Total steps in section (unused in base)
   */
  updateTextContent(
    _section,
    _sectionTitle,
    _stepDescription,
    _stepNumber,
    _totalSteps,
  ) {
    throw new Error('updateTextContent must be implemented by subclass');
  }

  /**
   * Update step image with loader (view-specific implementation)
   * @abstract
   * @param {Element} _section - Section element (unused in base)
   * @param {Object} _stepItem - Step item data (unused in base)
   */
  updateStepImage(_section, _stepItem) {
    throw new Error('updateStepImage must be implemented by subclass');
  }

  /**
   * Calculate navigation context information
   * @param {Element} section - Section element
   * @param {Object} sectionData - Section data
   * @param {Object} fragmentData - Fragment data
   * @returns {Object} Navigation context
   */
  calculateNavigationContext(section, sectionData, fragmentData) {
    const currentStep = Number.parseInt(section.dataset.step, 10) || 1;
    const sectionIndex = Number.parseInt(section.dataset.section, 10);
    const currentSectionTotalStepCount = sectionData.step.length;
    const isLastSection = sectionIndex === fragmentData.section.length - 1;

    // Calculate total steps across all sections
    const totalStepsAllSections = fragmentData.section.reduce(
      (total, sectionItem) => total + sectionItem.step.length,
      0,
    );
    // For last section, we have an extra step for modal
    const effectiveTotalSteps = isLastSection
      ? totalStepsAllSections + 1
      : totalStepsAllSections;

    return {
      currentStep,
      sectionIndex,
      currentSectionTotalStepCount,
      isLastSection,
      effectiveTotalSteps,
    };
  }

  /**
   * Update previous button state and label
   * @param {Element} prevButton - Previous button element
   * @param {Object} context - Navigation context
   * @param {Object} fragmentData - Fragment data
   */
  updatePrevButton(prevButton, context, fragmentData) {
    if (!prevButton) {
      return;
    }

    const { currentStep, sectionIndex } = context;
    const prevStepLabel = this.buildPrevStepLabel(
      currentStep,
      sectionIndex,
      fragmentData,
    );

    prevButton.setAttribute('aria-label', prevStepLabel);
    const disablePrev = currentStep === 1 && sectionIndex === 0;
    if (disablePrev) {
      this.moveFocus(prevButton, 'prev');
    }
    this.setDisabledState(prevButton, disablePrev);
  }

  /**
   * Update next button state and label
   * @param {Element} nextButton - Next button element
   * @param {Object} context - Navigation context
   * @param {Object} fragmentData - Fragment data
   */
  updateNextButton(nextButton, context, fragmentData) {
    if (!nextButton) {
      return;
    }

    const {
      currentStep,
      sectionIndex,
      currentSectionTotalStepCount,
      isLastSection,
      effectiveTotalSteps,
    } = context;
    const nextStepLabel = this.buildNextStepLabel(
      currentStep,
      sectionIndex,
      fragmentData,
    );

    nextButton.setAttribute('aria-label', nextStepLabel);
    const nextSection = fragmentData?.section?.[sectionIndex + 1];
    const isOnExtraStep =
      isLastSection && currentStep > currentSectionTotalStepCount;
    const isLastActualStep = currentStep === effectiveTotalSteps;
    const disableNext = (isLastActualStep && !nextSection) || isOnExtraStep;
    if (disableNext) {
      this.moveFocus(nextButton, 'next');
    }
    this.setDisabledState(nextButton, disableNext);
  }

  /**
   * Update navigation buttons aria-labels with new step numbers
   * @param {Element} section - Section element
   * @param {Object} sectionData - Section data
   * @param {Object} fragmentData - Fragment data
   */
  updateNavButtonsAriaLabels(section, sectionData, fragmentData) {
    const { prevButton, nextButton } = this.getNavButtons(section);
    const context = this.calculateNavigationContext(
      section,
      sectionData,
      fragmentData,
    );

    this.updatePrevButton(prevButton, context, fragmentData);
    this.updateNextButton(nextButton, context, fragmentData);
  }

  /**
   * Get the previous/next button elements from a section.
   * @param {Element} section - Section element
   * @returns {{prevButton: Element|null, nextButton: Element|null}} Buttons or null if not found
   */
  getNavButtons(section) {
    const prev = section.querySelector(this.selectors.PREV_STEP_BUTTON);
    const next = section.querySelector(this.selectors.NEXT_STEP_BUTTON);
    return { prevButton: prev, nextButton: next };
  }

  /**
   * Build aria-label for the previous navigation button.
   * @param {number} currentStep - Current step number
   * @param {number} sectionIndex - Current section index
   * @param {Object} fragmentData - All fragment data (contains sections)
   * @returns {string} aria-label for prev button
   */
  buildPrevStepLabel(currentStep, sectionIndex, fragmentData) {
    const goToLabel = this.i18next.t(
      'MXP.Navigation.Accessibility.Navigation.Label',
      'Go to',
    );
    const goToPreviousStepLabel = `${goToLabel} ${this.i18next.t('MXP.Navigation.Accessibility.PreviousStep.Title', 'previous step')}`;
    const stepCounterLabel = this.i18next.t(COUNTER_LABEL_PLACEHOLDER_KEY, '');
    const stepTitle = this.i18next.t(STEP_TITLE_PLACEHOLDER_KEY, 'Step');

    const isLastSection = sectionIndex === fragmentData.section.length - 1;
    const currentSection = fragmentData.section[sectionIndex];
    const actualStepsInSection = currentSection
      ? currentSection.step.length
      : 0;

    if (!actualStepsInSection) {
      return goToPreviousStepLabel;
    }

    if (currentStep === 1) {
      if (sectionIndex > 0) {
        const prevSection = fragmentData.section[sectionIndex - 1];
        const { section_title: prevSectionTitle } = prevSection;
        const prevSectionSlideCount = prevSection.step.length;
        return `${goToLabel} ${prevSectionTitle} ${stepTitle} ${prevSectionSlideCount}`;
      }
      return goToPreviousStepLabel;
    }

    const localTotalSteps = isLastSection
      ? actualStepsInSection + 1
      : actualStepsInSection;
    const phrase = formatPhraseWithParams(stepCounterLabel, {
      steps: localTotalSteps,
    });

    return `${goToLabel} ${stepTitle} ${currentStep - 1} ${phrase}`;
  }

  /**
   * Build aria-label for the next navigation button.
   * @param {number} currentStep - Current step number
   * @param {number} sectionIndex - Current section index
   * @param {Object} fragmentData - All fragment data (contains sections)
   * @returns {string} aria-label for next button
   */
  buildNextStepLabel(currentStep, sectionIndex, fragmentData) {
    const goToLabel = this.i18next.t(
      'MXP.Navigation.Accessibility.Navigation.Label',
      'Go to',
    );
    const stepTitle = this.i18next.t(STEP_TITLE_PLACEHOLDER_KEY, 'Step');

    const isLastSection = sectionIndex === fragmentData.section.length - 1;
    const currentSection = fragmentData.section[sectionIndex];
    const actualStepsInSection = currentSection
      ? currentSection.step.length
      : 0;

    // Check if we're on the last step of current section and there's a next section
    if (currentStep === actualStepsInSection && !isLastSection) {
      const nextSection = fragmentData?.section[sectionIndex + 1];
      if (nextSection) {
        const { section_title: nextSectionTitle } = nextSection;
        return `${goToLabel} ${nextSectionTitle} ${stepTitle} 1`;
      }
    }

    const nextStepLabel = `${goToLabel} ${this.i18next.t('MXP.Navigation.Accessibility.NextStep.Title', 'next step')}`;

    if (!actualStepsInSection) {
      return nextStepLabel;
    }

    // On last section's last step or extra/modal step — return generic label
    if (isLastSection && currentStep >= actualStepsInSection) {
      return nextStepLabel;
    }

    // Calculate local step number for aria-label
    const localTotalSteps = isLastSection
      ? actualStepsInSection + 1
      : actualStepsInSection;
    const phrase = formatPhraseWithParams(
      this.i18next.t(COUNTER_LABEL_PLACEHOLDER_KEY, ''),
      { steps: localTotalSteps },
    );

    return `${goToLabel} ${stepTitle} ${currentStep + 1} ${phrase}`;
  }

  /**
   * Generic section/accordion creation template method
   * @param {Object} config - Section configuration
   * @returns {Element} Created section element
   */
  createSectionBase(config) {
    const {
      isOverview,
      data,
      sectionClass,
      sectionNum,
      stepNum,
      buttonProps = {},
      contentProps = {},
      contentElements = [],
    } = config;

    // eslint-disable-next-line no-restricted-syntax -- SonarQube prefers for...of
    // but ESLint config doesn't allow it
    contentElements.forEach((el) => {
      el.innerHTML = el.innerHTML
        .replaceAll('&lt;', '<')
        .replaceAll('&gt;', '>');
    });

    contentElements.forEach((el) => {
      const container = el.classList?.contains('gt_content-container')
        ? el
        : el.querySelector('.gt_content-container');
      if (container) {
        appendLoaderToContainer(container);
      }
    });

    const title = this.getSectionTitle(isOverview, data, stepNum);
    const sectionAttributes = this.buildSectionAttributes(
      isOverview,
      sectionClass,
      sectionNum,
      stepNum,
    );
    const buttonAttributes = this.buildButtonAttributes(
      isOverview,
      title,
      buttonProps,
    );

    return this.createSectionDOM(
      sectionAttributes,
      buttonAttributes,
      title,
      contentProps,
      contentElements,
    );
  }

  /**
   * Get section title based on type and data
   * @param {boolean} isOverview - Whether this is overview section
   * @param {Object} data - Section data
   * @param {number} stepNum - Step number
   * @returns {string} Section title
   */
  getSectionTitle(isOverview, data, stepNum) {
    if (isOverview) {
      return this.i18next.t('MXP.Overview.Section.Title.Text', 'Overview');
    }

    return (
      data.section_title ||
      data?.step?.[stepNum - 1]?.title ||
      this.i18next.t(UNTITLED_SECTION_PLACEHOLDER_KEY, 'Untitled')
    );
  }

  /**
   * Build section attributes
   * @param {boolean} isOverview - Whether this is overview section
   * @param {string} sectionClass - CSS class for section
   * @param {number} sectionNum - Section number
   * @param {number} stepNum - Step number
   * @returns {Object} Section attributes
   */
  buildSectionAttributes(isOverview, sectionClass, sectionNum, stepNum) {
    const attributes = {
      class: sectionClass,
      role: 'listitem',
    };

    if (!isOverview) {
      attributes['data-step'] = stepNum;
      attributes[SELECTORS.DATA_SECTION] = sectionNum;
    }

    return attributes;
  }

  /**
   * Build button attributes with analytics
   * @param {boolean} isOverview - Whether this is overview section
   * @param {string} title - Section title
   * @param {Object} buttonProps - Additional button properties
   * @returns {Object} Button attributes
   */
  buildButtonAttributes(isOverview, title, buttonProps) {
    return {
      'aria-label': isOverview
        ? this.i18next.t('MXP.Overview.Section.Button.Label', 'Overview')
        : `${title} item`,
      ...this.getAAAttributes(
        isOverview ? this.AA_CONSTANTS.LINKS.OVERVIEW : title,
        isOverview ? this.AA_CONSTANTS.LINKS.OVERVIEW : title,
      ),
      ...buttonProps,
    };
  }

  /**
   * Create the actual DOM structure (view-specific implementation)
   * @abstract
   * @param {Object} _sectionAttributes - Section attributes (unused in base)
   * @param {Object} _buttonAttributes - Button attributes (unused in base)
   * @param {string} _title - Section title (unused in base)
   * @param {Object} _contentAttributes - Content attributes (unused in base)
   * @param {Array} _contentElements - Content elements (unused in base)
   * @returns {Element} Created DOM element
   */
  createSectionDOM(
    _sectionAttributes,
    _buttonAttributes,
    _title,
    _contentAttributes,
    _contentElements,
  ) {
    throw new Error('createSectionDOM must be implemented by subclass');
  }

  /**
   * Shared section decoration logic
   * @param {Object} data - The section data
   * @param {number} sectionNum - The section number
   * @param {number} stepNum - The step number
   * @param {boolean} isLastSection - Whether this is the last section
   * @returns {Element|string} The decorated section element or empty string
   */
  decorateSectionBase(data, sectionNum, stepNum = 1, isLastSection = false) {
    // Shared validation logic
    if (!this.validateStepData(data, stepNum)) {
      return '';
    }

    const stepContent = this.extractStepContent(data, stepNum);
    if (!stepContent) {
      return '';
    }

    const { stepItem, sectionTitle, stepDescription } = stepContent;
    // For last section, add extra step for modal display
    const totalSteps = isLastSection ? data.step.length + 1 : data.step.length;

    // Get view-specific configuration
    const config = this.buildSectionConfig(
      data,
      sectionNum,
      stepNum,
      stepContent,
      totalSteps,
    );

    // Create view-specific content elements
    config.contentElements = this.createSectionContentElements(
      stepItem,
      stepDescription,
      sectionTitle,
      stepNum,
      totalSteps,
      data.step,
    );

    // Use the existing createSectionBase method
    return this.createSectionBase(config);
  }

  /**
   * Build section configuration (view-specific implementation)
   * @abstract
   * @param {Object} _data - Section data (unused in base)
   * @param {number} _sectionNum - Section number (unused in base)
   * @param {number} _stepNum - Step number (unused in base)
   * @param {Object} _stepContent - Step content (unused in base)
   * @param {number} _totalSteps - Total steps (unused in base)
   * @returns {Object} Section configuration
   */
  buildSectionConfig(_data, _sectionNum, _stepNum, _stepContent, _totalSteps) {
    throw new Error('buildSectionConfig must be implemented by subclass');
  }

  /**
   * Create section content elements (view-specific implementation)
   * @abstract
   * @param {Object} _stepItem - Step item data (unused in desktop, used in mobile)
   * @param {string} _stepDescription - Step description (unused in base)
   * @param {string} _sectionTitle - Section title (unused in base)
   * @param {number} _stepNum - Step number (unused in base)
   * @param {number} _totalSteps - Total steps (unused in base)
   * @returns {Array} Content elements
   */
  createSectionContentElements(
    _stepItem,
    _stepDescription,
    _sectionTitle,
    _stepNum,
    _totalSteps,
  ) {
    throw new Error(
      'createSectionContentElements must be implemented by subclass',
    );
  }

  /**
   * Get view-specific section CSS class
   * @abstract
   * @returns {string} Section CSS class
   */
  getSectionClass() {
    throw new Error('getSectionClass must be implemented by subclass');
  }

  /**
   * Get view-specific content ID prefix
   * @abstract
   * @returns {string} Content ID prefix (e.g., 'contentDesktop' or 'contentMobile')
   */
  getContentIdPrefix() {
    throw new Error('getContentIdPrefix must be implemented by subclass');
  }

  /**
   * Build section configuration using template method pattern
   * @param {Object} data - Section data
   * @param {number} sectionNum - Section number
   * @param {number} stepNum - Step number
   * @param {Object} stepContent - Step content
   * @param {number} totalSteps - Total steps
   * @returns {Object} Section configuration
   */
  buildSectionConfigBase(data, sectionNum, stepNum, stepContent, totalSteps) {
    const { sectionTitle } = stepContent;
    const contentIdPrefix = this.getContentIdPrefix();
    const contentAriaLabel = this.i18next.t(
      'MXP.Section.Accessibility.Step.Content.Steps.Template',
      '',
    );

    return {
      data,
      sectionNum,
      stepNum,
      isOverview: false,
      sectionClass: this.getSectionClass(),
      buttonProps: {
        'aria-expanded': 'false',
        'aria-controls': `${contentIdPrefix}${stepNum}`,
      },
      contentProps: {
        id: `${contentIdPrefix}${stepNum}`,
        hidden: true,
        'aria-label': contentAriaLabel
          ? formatPhraseWithParams(contentAriaLabel, {
              title: sectionTitle,
              steps: totalSteps,
            })
          : '',
      },
    };
  }

  /**
   * Create navigation buttons (shared implementation)
   * @param {string} sectionTitle - Section title for analytics
   * @param {number} stepNum - Current step number
   * @param {number} totalSteps - Total number of steps
   * @returns {Element} Navigation buttons container
   */
  createNavigationButtons(sectionTitle, stepNum, totalSteps) {
    return div(
      { class: 'gt_controls' },
      button(
        {
          class: 'tour-prev gt_button-icon',
          'aria-label': this.i18next.t(
            'MXP.Navigation.Accessibility.PreviousStep.Title',
            'Previous step',
          ),
          ...this.getAAAttributes(
            this.AA_CONSTANTS.LINKS.PREVIOUS_STEP,
            sectionTitle,
          ),
        },
        decoratePreviousSVG(),
      ),
      p({ class: '' }, ...this.createStepIndicator(stepNum, totalSteps)),
      button(
        {
          class: 'tour-next gt_button-icon',
          'aria-label': this.i18next.t(
            'MXP.Navigation.Accessibility.NextStep.Title',
            'Next step',
          ),
          ...this.getAAAttributes(
            this.AA_CONSTANTS.LINKS.NEXT_STEP,
            sectionTitle,
          ),
        },
        decorateNextSVG(),
      ),
    );
  }
}

export default ProductTourBaseEnhanced;
